import { __assign } from "tslib";
import { reduxKey } from './constants';
import { ThemeOptions, } from '@wix/da-react-context/pkg/ThemeContext';
import { getUserById } from '../redux/users/selectors';
import { flagEnabled } from '../redux/flags/selectors';
import { HubChannel } from '../redux/hub/types';
import { createSelector } from 'reselect';
import get from 'lodash/get';
import { parseIsoDateOrTimestamp } from '../Timestamp/functions';
export var getPublicSession = function (state) {
    var publicSession = state && state[reduxKey] ? state[reduxKey] : {};
    var user;
    if (publicSession.user) {
        if (typeof publicSession.user === 'object') {
            user = publicSession.user;
        }
        else {
            user = getUserById(state, publicSession.user);
        }
    }
    return __assign(__assign({}, publicSession), { user: user });
};
export var getCsrfToken = function (state) {
    return getPublicSession(state).csrfToken;
};
export var getIsMobile = function (state) { return getPublicSession(state).isMobile; };
export var getIsIOS = function (state) { var _a; return !!((_a = getPublicSession(state).device) === null || _a === void 0 ? void 0 : _a.isIos); };
export var getPagingMode = function (state) {
    return getPublicSession(state).pagingMode === 'page' ? 'page' : 'scroll';
};
export var getPageSize = function (state) {
    return get(getPublicSession(state), 'pageSize', 24);
};
export var getIsLoggedIn = function (state) {
    return getPublicSession(state).isLoggedIn;
};
export var getIsVerificationNeeded = function (state) {
    return getIsLoggedIn(state) && !getPublicSession(state).isVerified;
};
export var getIsVerified = function (state) {
    return getIsLoggedIn(state) && getPublicSession(state).isVerified;
};
export var getIsUnverified = function (state) {
    return getIsLoggedIn(state) && getPublicSession(state).isUnverified;
};
export var getVerificationEmailAddress = function (state) {
    return getPublicSession(state).verificationEmail;
};
export var getCurrentUser = function (state) {
    var publicSession = getPublicSession(state);
    return publicSession.user;
};
export var getCurrentUserHasWixPortfolio = function (state) {
    var publicSession = getPublicSession(state);
    return publicSession.hasWixPortfolio;
};
export var getCurrentUserId = function (state) {
    var currentUser = getCurrentUser(state);
    return currentUser && currentUser.userId;
};
export var getSeo = function (state) { return getPublicSession(state).seo; };
export var getSeoCanonical = function (state) {
    var seo = getSeo(state);
    return seo && seo.canonical;
};
export var getSeoTitle = function (state) {
    var seo = getSeo(state);
    return seo && seo.title
        ? seo.title
        : 'DeviantArt - Discover The Largest Online Art Gallery and Community';
};
export var getSeoDescription = function (state) {
    var seo = getSeo(state);
    return seo && seo.description;
};
export var getSeoPrevUrl = function (state) { var _a; return (_a = getSeo(state)) === null || _a === void 0 ? void 0 : _a.prev; };
export var getSeoNextUrl = function (state) { var _a; return (_a = getSeo(state)) === null || _a === void 0 ? void 0 : _a.next; };
export var getCounts = function (state) {
    return getPublicSession(state).counts;
};
export var getWatchCount = function (state) {
    return (getCounts(state) && getCounts(state).watch) || 0;
};
export var getNotesCount = function (state) {
    return (getCounts(state) && getCounts(state).notes) || 0;
};
export var getFeedbackCount = function (state) {
    return (getCounts(state) && getCounts(state).feedback) || 0;
};
export var getFeedbackUrl = function (state) {
    return getPublicSession(state).feedbackUrl || '';
};
export var getCartCount = function (state) {
    return (getCounts(state) && getCounts(state).cart) || 0;
};
export var getTierСount = function (state) {
    return (getCounts(state) && getCounts(state).tiers) || 0;
};
export var getPointsCount = function (state) {
    return (getCounts(state) && getCounts(state).points) || 0;
};
export var getDywHasNew = function (state) {
    return (getCounts(state) && getCounts(state).dywHasNew) || false;
};
export var getCurrentUserHasCore = function (state) {
    return getPublicSession(state).hasCoreMembership;
};
export var getCoreLevel = function (state) {
    return getPublicSession(state).coreLevel;
};
export var getHasJoinedWithEclipse = function (state) {
    return getPublicSession(state).hasJoinedWithEclipse;
};
export function getViewMode(state) {
    return getPublicSession(state).deviationViewMode;
}
export var getHubState = function (state) {
    return getPublicSession(state).hub;
};
export var getHasAcceptedCookies = function (state) {
    return getPublicSession(state).hasAcceptedCookies;
};
export var getCookieSettings = function (state) {
    return getPublicSession(state).cookiePrivacySettings;
};
export var getCookiePrivacyRequired = function (state) {
    return getPublicSession(state).cookiePrivacyRequired;
};
export var getIsCookieBannerVisible = function (state) {
    return getCookiePrivacyRequired(state) && !getHasAcceptedCookies(state);
};
export var getLiveNotificationsEnabled = function (state) {
    var _a;
    var hubState = getHubState(state);
    var subs = hubState && hubState.subs;
    return ((_a = subs === null || subs === void 0 ? void 0 : subs.some(function (sub) { return sub.startsWith("".concat(HubChannel.NOTIFICATIONS, "#")); })) !== null && _a !== void 0 ? _a : false);
};
export var getDRE = function (state) {
    return getPublicSession(state).dre;
};
export var getShowMatureContent = createSelector([getPublicSession], function (session) { return session.showMatureContent; });
export var isUserOlderThan18 = createSelector([getCurrentUser], function (user) { return get(user, 'details.age', 0) >= 18; });
export var getCanEnableToggleMatureContent = isUserOlderThan18;
export var getCurrentUserCommentsOrder = createSelector([getPublicSession], function (session) {
    if (!session || !session.commentsOrder) {
        return 'newest';
    }
    return session.commentsOrder;
});
export var getTheme = createSelector([getPublicSession], function (session) {
    return (session && session.theme) || ThemeOptions.DARK;
});
export var getThemeSkin = createSelector([getPublicSession], function (session) {
    return session && session.themeSkin;
});
export var getPageThumb = createSelector([getPublicSession], function (session) {
    return get(session, 'seo.socialThumbSrc');
});
export var getVerificationLoadingState = createSelector([getPublicSession], function (_a) {
    var _b = _a.updateProgress, updateProgress = _b === void 0 ? {} : _b;
    return ({
        isResendInProgress: !!updateProgress.isResendInProgress,
        isEmailChangeInProgress: !!updateProgress.isEmailChangeInProgress,
        isSendingDone: !!updateProgress.isSendingDone,
        updateFailed: !!updateProgress.updateFailed,
    });
});
export var getIsEclipseSwitcherEnabled = createSelector([getPublicSession], function (session) {
    if (!session || !session.eclipseSwitcher) {
        return false;
    }
    return session.eclipseSwitcher;
});
export var getDeviceSupportsWebp = function (state) {
    var publicSession = getPublicSession(state);
    return get(publicSession, 'device.supportsWebp');
};
export var getDeviceSupportsLazy = function (state) {
    var publicSession = getPublicSession(state);
    return get(publicSession, 'device.supportsLazy');
};
// Chat
export var getChatIsMuted = function (state) {
    var publicSession = getPublicSession(state);
    return get(publicSession, 'chat.muted', false) === true;
};
export var getChatOnlineState = function (state) {
    var publicSession = getPublicSession(state);
    return get(publicSession, 'chat.online', true);
};
export var getChatSoundsMuted = function (state) {
    var publicSession = getPublicSession(state);
    return get(publicSession, 'chat.muteSound', false);
};
export var getFragmentsBalance = function (state) {
    var _a;
    var counts = getCounts(state);
    var defaultBalance = flagEnabled(state, 'eclipse_debug') ? 1000 : 0;
    return (_a = counts === null || counts === void 0 ? void 0 : counts.fragments) !== null && _a !== void 0 ? _a : defaultBalance;
};
var CAN_SEE_WATCH_CUTOFF = 1601503200000; // 2020-10-01
export var getCanUserSeeWatch = function (state) {
    if (flagEnabled(state, 'oldwatch_forall')) {
        return true;
    }
    var joinDate = getPublicSession(state).joinDate;
    if (!joinDate) {
        return false;
    }
    return CAN_SEE_WATCH_CUTOFF - parseIsoDateOrTimestamp(joinDate).getTime() > 0;
};
export var getPromos = createSelector([getPublicSession], function (session) { return session === null || session === void 0 ? void 0 : session.promos; });
export var getLimitedBadgesPromo = createSelector([getPublicSession], function (session) {
    return session === null || session === void 0 ? void 0 : session.limitedBadgesPromo;
});
export var getAdsEnabled = createSelector([getPublicSession], function (session) {
    return session === null || session === void 0 ? void 0 : session.adsEnabled;
});
//# sourceMappingURL=selectors.js.map